import { AddCommentResponseDto, CommentAttachment } from "@/dto/add-comment-response.dto";
import { CreateProjectRequestDto } from "@/dto/create-project-request.dto";
import { CreateProjectResponseDto } from "@/dto/create-project-response.dto";
import { CreateSubmissionRequestDto } from "@/dto/create-submission-request.dto";
import { CreateSubmissionResponseDto } from "@/dto/create-submission-response.dto";
import { GetProjectResponseDto } from "@/dto/get-project-response.dto";
import { GetProjectsResponseDto } from "@/dto/get-projects-response.dto";
import { ProjectStatus } from "@/enums/project.enum";
import { api } from "@/lib/api";

// Unified invitation types
export interface UnifiedProjectInvitation {
  _id: string;
  email: string;
  role: string;
  expiresAt: string;
  invitedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isNewUser: boolean;
  createdAt: string;
  acceptedAt: string | null;
  declinedAt: string | null;
  project: {
    _id: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
  };
}

export interface ProjectInvitationAcceptResponse {
  message: string;
  invitationId: string;
  projectId?: string;
  userId?: string;
  isNewUser: boolean;
  role: string;
}

export interface GetProjectsResponse {
  projects: GetProjectsResponseDto[];
}

export const projectApi = {
  createProject: async (
    dto: CreateProjectRequestDto
  ): Promise<CreateProjectResponseDto> => {
    const response = await api.post<CreateProjectResponseDto>("/projects", {
      ...dto,
    });
    return response.data;
  },
  updateProject: async (
    projectId: string,
    dto: Partial<CreateProjectRequestDto> & { permitNumber?: string; estimatedReviewDateByAHJ?: string }
  ) => {
    const response = await api.patch(`/projects/${projectId}` , dto);
    return response.data;
  },
  createSubmission: async (
    projectId: string,
    dto: CreateSubmissionRequestDto & { files?: Record<string, File | File[]> }
  ): Promise<CreateSubmissionResponseDto> => {
    const formData = new FormData();

    // Convert all primitive fields and JSON-stringify nested objects/arrays
    for (const [key, value] of Object.entries(dto)) {
      if (key === "files") continue; // Skip for now

      if (Array.isArray(value) || typeof value === "object") {
        formData.append(key, JSON.stringify(value));
      } else if (value !== undefined) {
        formData.append(key, String(value));
      }
    }

    // Append files if present
    if (dto.files) {
      console.log("=== PROJECT SERVICE DEBUG (CREATE) ===");
      console.log("Processing files:", dto.files);
      
      for (const [fieldName, fileOrFiles] of Object.entries(dto.files)) {
        console.log(`Processing field: ${fieldName}`, fileOrFiles);
        
        if (Array.isArray(fileOrFiles)) {
          console.log(`  - Array of ${fileOrFiles.length} files`);
          // Handle multiple files - append each with the same field name
          fileOrFiles.forEach((file, index) => {
            console.log(`  - Appending file ${index}:`, file.name);
            formData.append(fieldName, file);
          });
        } else if (fileOrFiles) {
          console.log(`  - Single file:`, fileOrFiles.name);
          // Handle single file
          formData.append(fieldName, fileOrFiles);
        }
      }
    }

    const response = await api.post<CreateSubmissionResponseDto>(
      `/projects/${projectId}/submissions`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  },
  updateSubmission: async (
    projectId: string,
    dto: CreateSubmissionRequestDto & { files?: Record<string, File | File[]> }
  ): Promise<CreateSubmissionResponseDto> => {
    const formData = new FormData();

    // Convert all primitive fields and JSON-stringify nested objects/arrays
    for (const [key, value] of Object.entries(dto)) {
      if (key === "files") continue; // Skip for now

      if (Array.isArray(value) || typeof value === "object") {
        formData.append(key, JSON.stringify(value));
      } else if (value !== undefined) {
        formData.append(key, String(value));
      }
    }

    // Append files if present
    if (dto.files) {
      console.log("=== PROJECT SERVICE DEBUG (UPDATE) ===");
      console.log("Processing files:", dto.files);
      
      for (const [fieldName, fileOrFiles] of Object.entries(dto.files)) {
        console.log(`Processing field: ${fieldName}`, fileOrFiles);
        
        if (Array.isArray(fileOrFiles)) {
          console.log(`  - Array of ${fileOrFiles.length} files`);
          // Handle multiple files - append each with the same field name
          fileOrFiles.forEach((file, index) => {
            console.log(`  - Appending file ${index}:`, file.name);
            formData.append(fieldName, file);
          });
        } else if (fileOrFiles) {
          console.log(`  - Single file:`, fileOrFiles.name);
          // Handle single file
          formData.append(fieldName, fileOrFiles);
        }
      }
    }

    const response = await api.put<CreateSubmissionResponseDto>(
      `/projects/${projectId}/submissions`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  },
  getProjects: async (): Promise<GetProjectsResponse> => {
    const resp = await api.get<GetProjectsResponse>("/projects");
    return resp.data;
  },
  getProject: async (projectId: string): Promise<GetProjectResponseDto> => {
    const resp = await api.get<GetProjectResponseDto>(`/projects/${projectId}`);
    return resp.data;
  },
  getProjectInvitations: async (projectId: string): Promise<UnifiedProjectInvitation[]> => {
    const resp = await api.get<UnifiedProjectInvitation[]>(`/projects/${projectId}/invitations`);
    return resp.data;
  },
  downloadProjectFiles: async (projectId: string): Promise<Blob> => {
    const resp = await api.get<Blob>(`/projects/${projectId}/download-files`, {
      responseType: "blob",
    });
    return resp.data;
  },
  validateProjectInvite: async (token: string) => {
    const response = await api.get(`/unified-invitations/validate/${token}`);
    return {
      isValid: true,
      invitationId: response.data.invitationId,
      email: response.data.email,
      role: response.data.role,
      project: response.data.projects?.[0] ? {
        name: response.data.projects[0].projectName,
        address: '', // Not provided in unified response
        city: response.data.projects[0].city,
        state: response.data.projects[0].state,
      } : null,
      projectId: response.data.projects?.[0]?.projectId,
      invitedBy: response.data.invitedBy,
    };
  },
  acceptProjectInvite: async (
    token: string
  ): Promise<ProjectInvitationAcceptResponse> => {
    const response = await api.post(`/unified-invitations/accept`, { token });
    return response.data;
  },
  declineProjectInvite: async (
    token: string
  ): Promise<ProjectInvitationAcceptResponse> => {
    const response = await api.post(`/unified-invitations/decline`, { token });
    return response.data;
  },
  removeMemeberFromProject: async (projectId: string, userId: string) => {
    const response = await api.delete(
      `projects/${projectId}/members/${userId}`
    );
    return response.data;
  },
  updateProjectStatus: async (
    projectId: string,
    status: ProjectStatus
  ): Promise<{ _id: string; status: ProjectStatus }> => {
    const response = await api.patch(`/projects/${projectId}/status`, {
      status,
    });
    return response.data;
  },
  addComment: async (
    projectId: string,
    content: string,
    richContent?: string,
    attachments?: File[],
    mentions?: string[],
    parentComment?: string,
    parentActivityId?: string
  ): Promise<AddCommentResponseDto> => {
    const formData = new FormData();
    
    formData.append('content', content);
    if (richContent) formData.append('richContent', richContent);
    if (mentions && mentions.length > 0) formData.append('mentions', JSON.stringify(mentions));
    if (parentComment) formData.append('parentComment', parentComment);
    if (parentActivityId) formData.append('parentActivityId', parentActivityId);
    
    // Add attachments
    if (attachments && attachments.length > 0) {
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });
    }

    const response = await api.post(
      `/projects/${projectId}/comment`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
  editComment: async (
    projectId: string,
    commentId: string,
    content: string,
    richContent?: string,
    attachments?: File[],
    replaceAttachments?: boolean
  ): Promise<{ message: string; comment: { _id: string; content: string; richContent?: string; attachments?: CommentAttachment[]; updatedAt: Date; isEdited: boolean } }> => {
    // Use FormData if attachments are being modified
    if (replaceAttachments !== undefined || (attachments && attachments.length > 0)) {
      const formData = new FormData();
      
      formData.append('content', content);
      if (richContent) formData.append('richContent', richContent);
      formData.append('replaceAttachments', String(replaceAttachments || false));
      
      // Add attachments
      if (attachments && attachments.length > 0) {
        attachments.forEach((file, index) => {
          formData.append(`attachment_${index}`, file);
        });
      }

      const response = await api.put(
        `/projects/${projectId}/comment/${commentId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } else {
      // Use JSON for text-only edits
      const response = await api.put(
        `/projects/${projectId}/comment/${commentId}`,
        {
          content,
          richContent,
        }
      );
      return response.data;
    }
  },
  deleteComment: async (
    projectId: string,
    commentId: string
  ): Promise<{ message: string }> => {
    const response = await api.delete(
      `/projects/${projectId}/comment/${commentId}`
    );
    return response.data;
  },
  uploadAdditionalDocument: async (
    projectId: string,
    file: File,
    tag: string
  ): Promise<{ message: string; document: { _id: string; name: string; tag: string; size: number; fileType: string; uploadedAt: string; uploadedBy: string; url: string } }> => {
    const formData = new FormData();
    formData.append("tag", tag);
    formData.append("document", file);

    const response = await api.post(
      `/projects/${projectId}/additional-documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },
  deleteAdditionalDocument: async (
    projectId: string,
    documentId: string
  ): Promise<{ message: string }> => {
    const response = await api.delete(
      `/projects/${projectId}/additional-documents/${documentId}`
    );
    return response.data;
  },
  uploadAHJDocument: async (
    projectId: string,
    requirementKey: string,
    file: File
  ): Promise<{ message: string; document: { _id: string; name: string; requirementKey: string; size: number; fileType: string; uploadedAt: string; uploadedBy: string; url: string } }> => {
    const formData = new FormData();
    formData.append("requirementKey", requirementKey);
    formData.append("document", file);

    const response = await api.post(
      `/projects/${projectId}/ahj-documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },
  deleteAHJDocument: async (
    projectId: string,
    documentId: string
  ): Promise<{ message: string }> => {
    const response = await api.delete(
      `/projects/${projectId}/ahj-documents/${documentId}`
    );
    return response.data;
  },
  restoreSubmission: async (
    projectId: string,
    statusHistoryId: string,
    fieldsToRestore?: string[]
  ): Promise<{ message: string; submissionId: string; restoredFields: number }> => {
    const response = await api.post(
      `/projects/${projectId}/submissions/restore`,
      { statusHistoryId, fieldsToRestore }
    );
    return response.data;
  },
};
